import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { SelectOptionRequestDto } from "@advanced-sales-technologies/backend-client/models/SelectOptionRequestDto";
import type { SimpleSelectOptionDto } from "@advanced-sales-technologies/backend-client/models/SimpleSelectOptionDto";
import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";
import { HiOutlineEnvelope } from "react-icons/hi2";

import { useGetCompanyEmailOptions } from "@/hooks/api/list/options/useGetCompanyEmailOptions";
import BaseColumn from "@/components/molecules/table/BaseColumn";
import HasMoreDataCellGeneral from "@/components/molecules/table/HasMoreDataCellGeneral";
import { searchColumns, SearchColumnsProps } from "@/components/organisms/search/searchColumns";

const companyEmailColumn = ({ t, listId, searchParams, queryKey }: SearchColumnsProps): ColumnDef<CompanyEntryDto> =>
  BaseColumn<CompanyEntryDto>({
    accessorFn: (row) => row.info.contact_info?.email?.value,
    id: searchColumns(t).contact_info_email.id,
    header: () => searchColumns(t).contact_info_email.name,
    cell: ({ row }) => {
      const company = row.original;
      const { id: companyId, info } = company;
      const contact = info.contact_info;

      if (!contact || !contact.email) return null;

      return (
        <HasMoreDataCellGeneral<SimpleSelectOptionDto>
          queryKey={queryKey}
          company={company}
          buildDisplayValue={(option) => option.value || ""}
          searchParams={searchParams}
          displayValue={contact.email.value}
          initialSelected={contact.email.id}
          listId={listId}
          target={SelectOptionRequestDto.target.COMPANY_EMAIL}
          hasMoreData={contact.email.has_more}
          placeholder={searchColumns(t).contact_info_email.name}
          useQueryHook={(query, enabled, searchParams) =>
            useGetCompanyEmailOptions(
              {
                companyId: companyId,
                option: {
                  query: query,
                  list_id: listId,
                  configuration: searchParams,
                },
              },
              enabled,
            )
          }
          renderValue={(value) => (
            <a href={`mailto:${value}`} target="_blank" rel="noreferrer" className={"text-link-gray underline"}>
              {value}
            </a>
          )}
          renderOption={(option, isSelected) => (
            <span
              key={option.id}
              className={"flex flex-1 flex-col gap-x-1.5 truncate py-2 text-left text-sm text-quaternary"}
            >
              {option.value && (
                <div className={clsx("truncate font-medium", isSelected ? "text-primary" : "text-secondary")}>
                  {option.value}
                </div>
              )}
            </span>
          )}
        />
      );
    },
    meta: {
      heading: {
        icon: HiOutlineEnvelope,
      },
    },
  });

export default companyEmailColumn;
